import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getDevices, getDeviceStats, getDeviceBundles, getDeviceFacets, getMotionHistogram, type SortDir } from '@/api/devices';
import { MultiSelect } from '@/components/MultiSelect';
import { Select } from '@/components/Select';
import { COUNTRIES } from '@/lib/countries';
import { formatDateTime } from '@/lib/datetime';
import type { DeviceReport, MotionBucket } from '@/api/devices';

const statusColors: Record<string, string> = {
  active: 'bg-success-soft text-success border-success/30',
  blocked: 'bg-danger-soft text-danger border-danger/30',
};

const reasonLabels: Record<string, string> = {
  none: '-',
  suspicious_apps: 'Suspicious Apps',
  no_sim: 'No SIM',
  geo_mismatch: 'GEO Mismatch',
  review_visit: 'Review / Emulator',
  motion_frozen: 'Frozen sensor',
  tz_mismatch: 'Timezone ≠ IP',
  battery_full: 'Battery 100% charging',
  device_burst: 'Same-model burst',
  bot_fleet: 'Scanner fleet build',
  behavior_bot: 'Bot by behavior',
};

const reasonColors: Record<string, string> = {
  none: 'text-content-subtle',
  suspicious_apps: 'text-danger',
  no_sim: 'text-warning',
  geo_mismatch: 'text-warning',
  review_visit: 'text-fuchsia-700',
  motion_frozen: 'text-danger',
  tz_mismatch: 'text-warning',
  battery_full: 'text-warning',
  device_burst: 'text-danger',
  bot_fleet: 'text-danger',
  behavior_bot: 'text-danger',
};

const outcomeStyles: Record<string, { label: string; className: string }> = {
  webview_shown:   { label: 'Shown',           className: 'bg-success-soft text-success border-success/30' },
  webview_failed:  { label: 'Render failed',   className: 'bg-danger-soft text-danger border-danger/30' },
  impression:      { label: 'Impression',      className: 'bg-lime-50 text-lime-700 border-lime-200' },
  dispatched:      { label: 'Dispatched',      className: 'bg-sky-50 text-sky-700 border-sky-200' },
  targeting_miss:  { label: 'Targeting miss',  className: 'bg-warning-soft text-warning border-warning/30' },
  no_active_ads:   { label: 'No ads',          className: 'bg-warning-soft text-warning border-warning/30' },
  version_review:  { label: 'In review',       className: 'bg-primary/10 text-primary border-primary/30' },
  review_banned:   { label: 'Review banned',   className: 'bg-fuchsia-100 text-fuchsia-800 border-fuchsia-300' },
  bad_credentials: { label: 'Bad credentials', className: 'bg-danger-soft text-danger border-danger/30' },
};

// Жёлтый, не красный: сами флаги — наблюдение. Блокирует только полностью
// замёрзшее окно, оно видно как причина блока «Frozen sensor».
const motionStyles: Record<string, { label: string; className: string }> = {
  ok:          { label: 'OK',          className: 'bg-surface-muted text-content-muted border-border' },
  static:      { label: 'Static',      className: 'bg-warning-soft text-warning border-warning/30' },
  no_sensor:   { label: 'No sensor',   className: 'bg-warning-soft text-warning border-warning/30' },
  gravity_off: { label: 'Gravity off', className: 'bg-warning-soft text-warning border-warning/30' },
  none:        { label: '—',           className: 'bg-surface-muted text-content-subtle border-border' },
};

const MOTION_OPTIONS = [
  { value: 'ok', label: 'Motion: OK' },
  { value: 'static', label: 'Motion: static' },
  { value: 'no_sensor', label: 'Motion: no sensor' },
  { value: 'gravity_off', label: 'Motion: gravity off' },
  { value: 'none', label: 'Motion: not reported' },
];

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'blocked', label: 'Blocked' },
];

const REASON_OPTIONS = [
  { value: 'none', label: 'No block' },
  { value: 'suspicious_apps', label: 'Suspicious Apps' },
  { value: 'no_sim', label: 'No SIM' },
  { value: 'geo_mismatch', label: 'GEO Mismatch' },
  { value: 'review_visit', label: 'Review / Emulator' },
  { value: 'motion_frozen', label: 'Frozen sensor' },
  { value: 'tz_mismatch', label: 'Timezone ≠ IP' },
  { value: 'battery_full', label: 'Battery 100% charging' },
  { value: 'device_burst', label: 'Same-model burst' },
  { value: 'bot_fleet', label: 'Scanner fleet build' },
  { value: 'behavior_bot', label: 'Bot by behavior' },
];

const OUTCOME_OPTIONS = [
  { value: 'webview_shown', label: 'Webview shown' },
  { value: 'webview_failed', label: 'Render failed' },
  { value: 'impression', label: 'Impression only' },
  { value: 'dispatched', label: 'Dispatched (no impr)' },
  { value: 'targeting_miss', label: 'Targeting miss' },
  { value: 'no_active_ads', label: 'No ads' },
  { value: 'version_review', label: 'In review' },
  { value: 'review_banned', label: 'Review banned' },
  { value: 'bad_credentials', label: 'Bad credentials' },
];

const SORT_OPTIONS = [
  { value: 'updated_at', label: 'Updated' },
  { value: 'created_at', label: 'First seen' },
  { value: 'last_ad_outcome_at', label: 'Last outcome at' },
  { value: 'device_id', label: 'Device ID' },
  { value: 'bundle_id', label: 'Bundle ID' },
  { value: 'has_sim', label: 'SIM presence' },
  { value: 'sim_country', label: 'SIM country' },
  { value: 'ip_country', label: 'IP country' },
  { value: 'geo_match', label: 'GEO match' },
  { value: 'sdk_status', label: 'Status' },
  { value: 'block_reason', label: 'Block reason' },
  { value: 'last_ad_outcome', label: 'Webview outcome' },
  { value: 'motion_flag', label: 'Motion flag' },
  { value: 'motion_stddev', label: 'Motion stddev' },
  { value: 'device_model', label: 'Device model' },
  { value: 'os_version', label: 'OS version' },
  { value: 'battery_level', label: 'Battery level' },
  { value: 'is_charging', label: 'Charging' },
  { value: 'screenshot_count', label: 'Screenshots taken' },
  { value: 'last_screenshot_at', label: 'Last screenshot at' },
];

const TRISTATE_HAS_SIM = [
  { value: 'all', label: 'SIM: any' },
  { value: 'yes', label: 'SIM: yes' },
  { value: 'no',  label: 'SIM: no' },
];
const TRISTATE_GEO = [
  { value: 'all',       label: 'GEO: any' },
  { value: 'match',     label: 'GEO: match' },
  { value: 'mismatch',  label: 'GEO: mismatch' },
];
const TRISTATE_CLEAN = [
  { value: 'all',   label: 'Device: any' },
  { value: 'clean', label: 'Device: clean' },
  { value: 'dirty', label: 'Device: dirty' },
];

const COUNTRY_OPTIONS = COUNTRIES.map((c) => ({
  value: c.code,
  label: `${c.flag} ${c.name} (${c.code})`,
}));

function OutcomeBadge({ outcome, failReason }: { outcome: string | null; failReason: string | null }) {
  if (!outcome) {
    return <span className="text-xs text-content-subtle">—</span>;
  }
  const style = outcomeStyles[outcome] || { label: outcome, className: 'bg-surface-muted text-content-muted border-border' };
  const title = outcome === 'webview_failed' && failReason ? `Reason: ${failReason}` : outcome;
  return (
    <span
      className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium border ${style.className}`}
      title={title}
    >
      {style.label}
      {outcome === 'webview_failed' && failReason ? ` · ${failReason}` : ''}
    </span>
  );
}

function MotionBadge({ d }: { d: DeviceReport }) {
  const flag = d.motion_flag || 'none';
  const style = motionStyles[flag] || { label: flag, className: 'bg-surface-muted text-content-muted border-border' };
  const title = flag === 'none'
    ? 'SDK этой версии не присылает акселерометр'
    : [
        `stddev: ${d.motion_stddev ?? '—'} мм/с²`,
        `dup: ${d.motion_dup_pct ?? '—'}%`,
        `gravity: ${d.motion_mean_g ?? '—'} мм/с²`,
        `posture: ${d.motion_posture || '—'}`,
        `events: ${d.motion_events ?? '—'}, samples: ${d.motion_samples ?? '—'}`,
        `gyro: ${d.motion_has_gyro ? 'yes' : 'no'}`,
      ].join('\n');
  return (
    <span className="inline-flex items-center gap-1.5" title={title}>
      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium border ${style.className}`}>
        {style.label}
      </span>
      {d.motion_stddev != null && (
        <span className="text-xs text-content-subtle tabular-nums">{d.motion_stddev}</span>
      )}
    </span>
  );
}

// Батарея приходит только со свежим SDK — у старых отчётов оба поля NULL,
// поэтому «нет данных» и «0% без зарядки» показываем по-разному.
/**
 * Вероятность бота по последней проверке «человек или бот». Нет проверки —
 * «—»: 0 читался бы как «точно человек». Мало данных — серым.
 */
function BotProbabilityCell({ p, confidence }: { p: number | null; confidence: number | null }) {
  if (p == null) return <span className="text-xs text-content-subtle">—</span>;
  const lowData = (confidence ?? 0) < 50;
  const color = lowData ? 'text-content-subtle' : p >= 60 ? 'text-danger' : p < 20 ? 'text-success' : 'text-warning';
  return (
    <span className={`text-sm font-medium tabular-nums ${color}`} title={`Data collected: ${confidence ?? 0}%`}>
      {p}%
    </span>
  );
}

function BatteryCell({ level, charging }: { level: number | null; charging: number | null }) {
  if (level == null && charging == null) {
    return <span className="text-xs text-content-subtle" title="SDK этой версии не присылает батарею">—</span>;
  }
  const levelColor =
    level == null ? 'text-content-subtle'
    : level <= 15 ? 'text-danger'
    : level <= 40 ? 'text-warning'
    : 'text-content-muted';
  return (
    <div className="whitespace-nowrap">
      <span className={`text-sm tabular-nums ${levelColor}`}>{level == null ? '—' : `${level}%`}</span>
      {charging != null && (
        <span className={`block text-xs ${charging ? 'text-success' : 'text-content-subtle'}`}>
          {charging ? 'Charging' : 'On battery'}
        </span>
      )}
    </div>
  );
}

function StatCard({ label, value, color, total }: { label: string; value: number; color: string; total?: number }) {
  const pct = total && total > 0 ? (value / total) * 100 : null;
  const pctText = pct == null ? null : pct >= 10 ? `${pct.toFixed(0)}%` : `${pct.toFixed(1)}%`;
  return (
    <div className="bg-surface rounded-xl border border-border shadow-sm p-5">
      <p className="text-xs font-medium text-content-subtle uppercase tracking-wider">{label}</p>
      <div className="mt-1 flex items-baseline gap-2">
        <p className={`text-2xl font-bold ${color}`}>{value.toLocaleString()}</p>
        {pctText && <span className="text-xs font-medium text-content-subtle">{pctText}</span>}
      </div>
    </div>
  );
}

const MOTION_BUCKET_ORDER = ['0', '1-4', '5-9', '10-24', '25-49', '50-99', '100+'];

/**
 * Распределение motion_stddev по бакетам — инструмент подбора порога
 * motion_stddev_min. Ожидаемая картина: горб в нуле (эмуляторы) и облако
 * в районе 10-49 (живые устройства); граница между ними и есть порог.
 * Тестовые устройства исключены на стороне сервера.
 */
function MotionHistogram({ buckets }: { buckets: MotionBucket[] }) {
  const byBucket = new Map(buckets.map((b) => [b.bucket, b.count]));
  const rows = MOTION_BUCKET_ORDER.map((b) => ({ bucket: b, count: byBucket.get(b) || 0 }));
  const max = Math.max(1, ...rows.map((r) => r.count));
  const total = rows.reduce((s, r) => s + r.count, 0);

  return (
    <div className="bg-surface rounded-xl border border-border shadow-sm p-5">
      <p className="text-xs font-medium text-content-subtle uppercase tracking-wider">
        Motion stddev, мм/с² · {total.toLocaleString()} devices
      </p>
      <p className="text-xs text-content-subtle mt-1">
        Живые устройства шумят даже лёжа на столе. Порог ставится по провалу между горбами. QA-устройства исключены.
      </p>
      {total === 0 ? (
        <p className="mt-3 text-sm text-content-subtle">Нет замеров — SDK с акселерометром ещё не доехал.</p>
      ) : (
        <div className="mt-3 space-y-1.5">
          {rows.map((r) => (
            <div key={r.bucket} className="flex items-center gap-3">
              <span className="w-16 text-xs text-content-subtle tabular-nums text-right">{r.bucket}</span>
              <div className="flex-1 h-4 bg-surface-muted rounded overflow-hidden">
                <div
                  className={`h-full ${r.bucket === '0' ? 'bg-warning' : 'bg-primary'}`}
                  style={{ width: `${(r.count / max) * 100}%` }}
                />
              </div>
              <span className="w-16 text-xs text-content-muted tabular-nums">{r.count.toLocaleString()}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function DevicesTab() {
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [reasonFilter, setReasonFilter] = useState<string[]>([]);
  const [outcomeFilter, setOutcomeFilter] = useState<string[]>([]);
  const [motionFilter, setMotionFilter] = useState<string[]>([]);
  const [ipCountryFilter, setIpCountryFilter] = useState<string[]>([]);
  const [simCountryFilter, setSimCountryFilter] = useState<string[]>([]);
  const [bundleFilter, setBundleFilter] = useState<string[]>([]);
  const [deviceModelFilter, setDeviceModelFilter] = useState<string[]>([]);
  const [osVersionFilter, setOsVersionFilter] = useState<string[]>([]);
  const [hasSim, setHasSim] = useState<string>('all');
  const [geoMatch, setGeoMatch] = useState<string>('all');
  const [deviceClean, setDeviceClean] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [dateField, setDateField] = useState<'updated_at' | 'created_at'>('updated_at');
  const [sortBy, setSortBy] = useState<string>('updated_at');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  // Filter-only params (no sort/pagination) — shared by the table query and the
  // summary so the cards above the table reflect the same filtered set.
  const filterParams = useMemo(() => ({
    status: statusFilter,
    reason: reasonFilter,
    outcome: outcomeFilter,
    motion: motionFilter,
    country: ipCountryFilter,
    simCountry: simCountryFilter,
    bundle: bundleFilter,
    deviceModel: deviceModelFilter,
    osVersion: osVersionFilter,
    hasSim: hasSim as 'all' | 'yes' | 'no',
    geoMatch: geoMatch as 'all' | 'match' | 'mismatch',
    deviceClean: deviceClean as 'all' | 'clean' | 'dirty',
    search,
    dateFrom: dateFrom ? new Date(dateFrom).toISOString() : undefined,
    dateTo: dateTo ? new Date(dateTo).toISOString() : undefined,
    dateField,
  }), [statusFilter, reasonFilter, outcomeFilter, motionFilter, ipCountryFilter, simCountryFilter, bundleFilter, deviceModelFilter, osVersionFilter, hasSim, geoMatch, deviceClean, search, dateFrom, dateTo, dateField]);

  const { data: stats } = useQuery({
    queryKey: ['device-stats', filterParams],
    queryFn: () => getDeviceStats(filterParams),
    refetchInterval: 30000,
  });

  const { data: bundles } = useQuery({
    queryKey: ['device-bundles'],
    queryFn: getDeviceBundles,
    refetchInterval: 60000,
  });

  const bundleOptions = useMemo(
    () => (bundles || []).map((b) => ({
      value: b.bundle_id,
      label: `${b.bundle_id} · ${b.count}`,
    })),
    [bundles]
  );

  const { data: facets } = useQuery({
    queryKey: ['device-facets'],
    queryFn: getDeviceFacets,
    refetchInterval: 60000,
  });

  // Nothing measured yet ⇒ skip the query entirely: device_reports has no
  // index on motion_stddev, so the CASE/GROUP BY full-scans the table, and
  // until an SDK build carrying the `motion` block reaches the store that
  // scan runs for nothing. We wait for stats rather than firing optimistically:
  // this is a derived expression, not state, so once stats resolves with a
  // measurement it flips to true and the query fetches on the enabled
  // transition — it cannot get stuck off. A population distribution doesn't
  // move minute to minute either, hence the longer refetch interval.
  const hasMotionMeasurements = !!stats && stats.total !== (stats.by_motion?.none ?? 0);
  const { data: motionBuckets } = useQuery({
    queryKey: ['motion-histogram', filterParams],
    queryFn: () => getMotionHistogram(filterParams),
    enabled: hasMotionMeasurements,
    refetchInterval: 5 * 60 * 1000,
  });

  const deviceModelOptions = useMemo(
    () => (facets?.device_models || []).map((m) => ({
      value: m.value,
      label: `${m.value} · ${m.count}`,
    })),
    [facets]
  );

  const osVersionOptions = useMemo(
    () => (facets?.os_versions || []).map((v) => ({
      value: v.value,
      label: `${v.value} · ${v.count}`,
    })),
    [facets]
  );

  const queryParams = useMemo(() => ({
    ...filterParams,
    sortBy,
    sortDir,
    page,
    limit: 50,
  }), [filterParams, sortBy, sortDir, page]);

  const { data: deviceList, isLoading } = useQuery({
    queryKey: ['devices', queryParams],
    queryFn: () => getDevices(queryParams),
    refetchInterval: 15000,
  });

  const devices = deviceList?.devices || [];
  const total = deviceList?.total || 0;
  const totalPages = Math.ceil(total / 50);

  const resetPage = <T,>(setter: (v: T) => void) => (v: T) => { setter(v); setPage(1); };

  const activeFiltersCount =
    statusFilter.length + reasonFilter.length + outcomeFilter.length + motionFilter.length +
    ipCountryFilter.length + simCountryFilter.length + bundleFilter.length +
    deviceModelFilter.length + osVersionFilter.length +
    (hasSim !== 'all' ? 1 : 0) + (geoMatch !== 'all' ? 1 : 0) + (deviceClean !== 'all' ? 1 : 0) +
    (search ? 1 : 0) + (dateFrom ? 1 : 0) + (dateTo ? 1 : 0);

  function clearAllFilters() {
    setStatusFilter([]);
    setReasonFilter([]);
    setOutcomeFilter([]);
    setMotionFilter([]);
    setIpCountryFilter([]);
    setSimCountryFilter([]);
    setBundleFilter([]);
    setDeviceModelFilter([]);
    setOsVersionFilter([]);
    setHasSim('all');
    setGeoMatch('all');
    setDeviceClean('all');
    setSearch('');
    setDateFrom('');
    setDateTo('');
    setPage(1);
  }

  function toggleSort(col: string) {
    if (sortBy === col) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(col);
      setSortDir('desc');
    }
    setPage(1);
  }

  const COLUMNS: { key: string; label: string; sortKey?: string }[] = [
    { key: 'device_id', label: 'Device ID', sortKey: 'device_id' },
    { key: 'bundle_id', label: 'Bundle ID', sortKey: 'bundle_id' },
    { key: 'has_sim', label: 'SIM', sortKey: 'has_sim' },
    { key: 'sim_country', label: 'SIM Country', sortKey: 'sim_country' },
    { key: 'ip_country', label: 'IP Country', sortKey: 'ip_country' },
    { key: 'geo_match', label: 'GEO', sortKey: 'geo_match' },
    { key: 'sdk_status', label: 'Status', sortKey: 'sdk_status' },
    { key: 'block_reason', label: 'Block Reason', sortKey: 'block_reason' },
    { key: 'last_ad_outcome', label: 'Webview', sortKey: 'last_ad_outcome' },
    { key: 'motion_flag', label: 'Motion', sortKey: 'motion_flag' },
    { key: 'battery_level', label: 'Battery', sortKey: 'battery_level' },
    { key: 'bot_probability', label: 'Bot probability' },
    { key: 'updated_at', label: 'Updated', sortKey: 'updated_at' },
  ];

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="bg-surface rounded-xl border border-border shadow-sm p-4 space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <input
            type="text"
            placeholder="Search by device ID or bundle ID..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="flex-1 min-w-[220px] px-3 py-2 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
          />
          <MultiSelect
            value={bundleFilter}
            onChange={resetPage(setBundleFilter)}
            options={bundleOptions}
            placeholder="Bundle ID"
            allLabel="All Bundles"
            searchable
            className="min-w-[320px]"
          />
          <MultiSelect
            value={deviceModelFilter}
            onChange={resetPage(setDeviceModelFilter)}
            options={deviceModelOptions}
            placeholder="Device model"
            allLabel="Any model"
            searchable
            className="min-w-[220px]"
          />
          <MultiSelect
            value={osVersionFilter}
            onChange={resetPage(setOsVersionFilter)}
            options={osVersionOptions}
            placeholder="OS version"
            allLabel="Any OS version"
            searchable
            className="min-w-[180px]"
          />
          <MultiSelect
            value={statusFilter}
            onChange={resetPage(setStatusFilter)}
            options={STATUS_OPTIONS}
            placeholder="Status"
            allLabel="All Status"
          />
          <MultiSelect
            value={reasonFilter}
            onChange={resetPage(setReasonFilter)}
            options={REASON_OPTIONS}
            placeholder="Block reason"
            allLabel="All Reasons"
          />
          <MultiSelect
            value={motionFilter}
            onChange={resetPage(setMotionFilter)}
            options={MOTION_OPTIONS}
            placeholder="Motion"
          />
          <MultiSelect
            value={outcomeFilter}
            onChange={resetPage(setOutcomeFilter)}
            options={OUTCOME_OPTIONS}
            placeholder="Webview outcome"
            allLabel="All Outcomes"
          />
          <MultiSelect
            value={ipCountryFilter}
            onChange={resetPage(setIpCountryFilter)}
            options={COUNTRY_OPTIONS}
            placeholder="IP country"
            allLabel="Any IP country"
            searchable
          />
          <MultiSelect
            value={simCountryFilter}
            onChange={resetPage(setSimCountryFilter)}
            options={COUNTRY_OPTIONS}
            placeholder="SIM country"
            allLabel="Any SIM country"
            searchable
          />
          <Select
            value={hasSim}
            onChange={resetPage(setHasSim)}
            options={TRISTATE_HAS_SIM}
            placeholder="SIM: any"
          />
          <Select
            value={geoMatch}
            onChange={resetPage(setGeoMatch)}
            options={TRISTATE_GEO}
            placeholder="GEO: any"
          />
          <Select
            value={deviceClean}
            onChange={resetPage(setDeviceClean)}
            options={TRISTATE_CLEAN}
            placeholder="Device: any"
          />
        </div>

        <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-border">
          <span className="text-xs font-medium text-content-subtle uppercase tracking-wider">Date</span>
          <Select
            value={dateField}
            onChange={(v) => { setDateField(v as 'updated_at' | 'created_at'); setPage(1); }}
            options={[
              { value: 'updated_at', label: 'Updated' },
              { value: 'created_at', label: 'First seen' },
            ]}
            placeholder="Date field"
          />
          <input
            type="datetime-local"
            value={dateFrom}
            onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
            className="px-2.5 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
          />
          <span className="text-content-subtle">—</span>
          <input
            type="datetime-local"
            value={dateTo}
            onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
            className="px-2.5 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
          />
          {(dateFrom || dateTo) && (
            <button
              type="button"
              onClick={() => { setDateFrom(''); setDateTo(''); setPage(1); }}
              className="text-xs text-content-subtle hover:text-primary"
            >
              Clear dates
            </button>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-border">
          <span className="text-xs font-medium text-content-subtle uppercase tracking-wider">Sort</span>
          <Select
            value={sortBy}
            onChange={(v) => { setSortBy(v); setPage(1); }}
            options={SORT_OPTIONS}
            placeholder="Sort by"
          />
          <button
            type="button"
            onClick={() => { setSortDir(sortDir === 'asc' ? 'desc' : 'asc'); setPage(1); }}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-border bg-surface text-content-muted hover:border-primary/40 hover:bg-primary/10 hover:text-primary transition-colors"
            title={sortDir === 'asc' ? 'Ascending' : 'Descending'}
          >
            {sortDir === 'asc' ? (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9M3 12h5m5 4l4-4 4 4m-4-4v12" />
                </svg>
                Asc
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9M3 12h9m4 0l4 4 4-4m-4 4V4" />
                </svg>
                Desc
              </>
            )}
          </button>
          {activeFiltersCount > 0 && (
            <button
              type="button"
              onClick={clearAllFilters}
              className="ml-auto text-sm text-primary hover:text-primary font-medium"
            >
              Clear filters ({activeFiltersCount})
            </button>
          )}
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
            <StatCard label="Total Devices" value={stats.total} color="text-content" />
            <StatCard label="Active" value={stats.active} color="text-success" total={stats.total} />
            <StatCard label="Blocked" value={stats.blocked} color="text-danger" total={stats.total} />
            <StatCard label="No SIM" value={stats.by_reason.no_sim} color="text-warning" total={stats.total} />
            <StatCard label="GEO Mismatch" value={stats.by_reason.geo_mismatch} color="text-warning" total={stats.total} />
            <StatCard label="Suspicious Apps" value={stats.by_reason.suspicious_apps} color="text-danger" total={stats.total} />
            <StatCard label="Review / Emulator" value={stats.by_reason.review_visit || 0} color="text-fuchsia-700" total={stats.total} />
            <StatCard label="Frozen sensor" value={stats.by_reason.motion_frozen || 0} color="text-danger" total={stats.total} />
            <StatCard label="Timezone ≠ IP" value={stats.by_reason.tz_mismatch || 0} color="text-warning" total={stats.total} />
            <StatCard label="Battery 100% charging" value={stats.by_reason.battery_full || 0} color="text-warning" total={stats.total} />
            <StatCard label="Same-model burst" value={stats.by_reason.device_burst || 0} color="text-danger" total={stats.total} />
            <StatCard label="Scanner fleet build" value={stats.by_reason.bot_fleet || 0} color="text-danger" total={stats.total} />
            <StatCard label="Motion: static" value={stats.by_motion?.static || 0} color="text-warning" total={stats.total} />
            <StatCard label="Motion: no sensor" value={stats.by_motion?.no_sensor || 0} color="text-warning" total={stats.total} />
            <StatCard label="Motion: gravity off" value={stats.by_motion?.gravity_off || 0} color="text-warning" total={stats.total} />
            <StatCard label="Motion: not reported" value={stats.by_motion?.none || 0} color="text-content-subtle" total={stats.total} />
          </div>
          {stats.by_outcome && (() => {
            const outcomeTotal = Object.values(stats.by_outcome).reduce((s, v) => s + (v || 0), 0);
            return (
              <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
                <StatCard label="Webview Shown" value={stats.by_outcome.webview_shown || 0} color="text-success" total={outcomeTotal} />
                <StatCard label="Render Failed" value={stats.by_outcome.webview_failed || 0} color="text-danger" total={outcomeTotal} />
                <StatCard label="Impression Only" value={stats.by_outcome.impression || 0} color="text-lime-600" total={outcomeTotal} />
                <StatCard label="Dispatched" value={stats.by_outcome.dispatched || 0} color="text-sky-600" total={outcomeTotal} />
                <StatCard label="Targeting Miss" value={stats.by_outcome.targeting_miss || 0} color="text-warning" total={outcomeTotal} />
                <StatCard label="No Ads" value={stats.by_outcome.no_active_ads || 0} color="text-warning" total={outcomeTotal} />
              </div>
            );
          })()}
        </>
      )}

      <MotionHistogram buckets={motionBuckets || []} />

      {/* Table */}
      <div className="bg-surface rounded-xl border border-border shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-content-subtle">Loading...</div>
        ) : devices.length === 0 ? (
          <div className="p-8 text-center text-content-subtle">No device reports yet</div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-surface-muted/50">
                  <tr>
                    {COLUMNS.map((col) => {
                      const isActive = col.sortKey && sortBy === col.sortKey;
                      return (
                        <th
                          key={col.key}
                          onClick={() => col.sortKey && toggleSort(col.sortKey)}
                          className={`px-4 py-3 text-xs font-semibold uppercase tracking-wider text-left select-none ${
                            col.sortKey ? 'cursor-pointer hover:text-primary' : ''
                          } ${isActive ? 'text-primary' : 'text-content-subtle'}`}
                        >
                          <span className="inline-flex items-center gap-1">
                            {col.label}
                            {col.sortKey && (
                              <span className="text-[10px]">
                                {isActive ? (sortDir === 'asc' ? '▲' : '▼') : '↕'}
                              </span>
                            )}
                          </span>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {devices.map((d: DeviceReport) => (
                    <>
                      <tr
                        key={d.id}
                        className={`transition-colors cursor-pointer ${
                          d.block_reason === 'review_visit'
                            ? 'bg-fuchsia-50/60 hover:bg-fuchsia-100/70'
                            : 'hover:bg-surface-muted/50'
                        }`}
                        onClick={() => setExpanded(prev => {
                          const next = new Set(prev);
                          if (next.has(d.id)) next.delete(d.id); else next.add(d.id);
                          return next;
                        })}
                      >
                        <td className="px-4 py-3 text-sm font-mono text-content-muted max-w-[120px] truncate" title={d.device_id}>
                          {d.device_id.substring(0, 12)}...
                        </td>
                        <td className="px-4 py-3 text-sm text-content-muted whitespace-nowrap" title={d.bundle_id}>
                          {d.bundle_id}
                        </td>
                        <td className="px-4 py-3">
                          {d.has_sim ? (
                            <span className="text-success text-sm">Yes</span>
                          ) : (
                            <span className="text-danger text-sm font-medium">No</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-content-muted">{d.sim_country || '-'}</td>
                        <td className="px-4 py-3 text-sm text-content-muted">{d.ip_country || '-'}</td>
                        <td className="px-4 py-3">
                          {d.geo_match ? (
                            <span className="text-success text-sm">Match</span>
                          ) : (
                            <span className="text-danger text-sm font-medium">Mismatch</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium border ${statusColors[d.sdk_status] || 'bg-surface-muted text-content-muted'}`}>
                            {d.sdk_status}
                          </span>
                        </td>
                        <td className={`px-4 py-3 text-sm font-medium ${reasonColors[d.block_reason] || 'text-content-subtle'}`}>
                          {reasonLabels[d.block_reason] || d.block_reason}
                        </td>
                        <td className="px-4 py-3">
                          <OutcomeBadge outcome={d.last_ad_outcome} failReason={d.webview_fail_reason} />
                        </td>
                        <td className="px-4 py-3">
                          <MotionBadge d={d} />
                        </td>
                        <td className="px-4 py-3">
                          <BatteryCell level={d.battery_level} charging={d.is_charging} />
                        </td>
                        <td className="px-4 py-3">
                          <BotProbabilityCell p={d.session_bot_probability} confidence={d.session_confidence} />
                        </td>
                        <td className="px-4 py-3 text-xs text-content-subtle">
                          {formatDateTime(d.updated_at)}
                        </td>
                      </tr>
                      {expanded.has(d.id) && (
                        <tr key={d.id + '-detail'}>
                          <td colSpan={COLUMNS.length} className="px-4 py-4 bg-surface-muted/80">
                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                              <div>
                                <p className="text-xs text-content-subtle mb-1">Full Device ID</p>
                                <p className="font-mono text-content-muted break-all">{d.device_id}</p>
                              </div>
                              <div>
                                <p className="text-xs text-content-subtle mb-1">Device Model</p>
                                <p className="text-content-muted">{d.device_model || 'Unknown'}</p>
                              </div>
                              <div>
                                <p className="text-xs text-content-subtle mb-1">OS Version</p>
                                <p className="text-content-muted">{d.os_version || 'Unknown'}</p>
                              </div>
                              <div>
                                <p className="text-xs text-content-subtle mb-1">Battery</p>
                                <p className="text-content-muted">
                                  {d.battery_level == null && d.is_charging == null
                                    ? '—'
                                    : `${d.battery_level == null ? '—' : `${d.battery_level}%`}${
                                        d.is_charging == null ? '' : d.is_charging ? ' · charging' : ' · on battery'
                                      }`}
                                </p>
                              </div>
                              <div>
                                <p className="text-xs text-content-subtle mb-1">First Seen</p>
                                <p className="text-content-muted">{formatDateTime(d.created_at)}</p>
                              </div>
                              {/* Подробности — на вкладке Screenshots, здесь только сам факт. */}
                              <div>
                                <p className="text-xs text-content-subtle mb-1">Screenshots</p>
                                <p className="text-content-muted">
                                  {d.screenshot_count
                                    ? `${d.screenshot_count} · last ${formatDateTime(d.last_screenshot_at)}`
                                    : '—'}
                                </p>
                              </div>
                              {d.last_ad_outcome && (
                                <>
                                  <div>
                                    <p className="text-xs text-content-subtle mb-1">Last Outcome</p>
                                    <OutcomeBadge outcome={d.last_ad_outcome} failReason={d.webview_fail_reason} />
                                  </div>
                                  <div>
                                    <p className="text-xs text-content-subtle mb-1">Outcome At</p>
                                    <p className="text-content-muted">{formatDateTime(d.last_ad_outcome_at)}</p>
                                  </div>
                                  <div>
                                    <p className="text-xs text-content-subtle mb-1">Last Ad</p>
                                    <p className="font-mono text-content-muted text-xs break-all">{d.last_ad_id || '—'}</p>
                                  </div>
                                  <div>
                                    <p className="text-xs text-content-subtle mb-1">Webview Shown At</p>
                                    <p className="text-content-muted">{formatDateTime(d.webview_shown_at)}</p>
                                  </div>
                                </>
                              )}
                              {/* Новые SDK шлют только флаг (detected_packages: true), без имён пакетов. */}
                              {!d.detected_packages && d.device_clean === 0 && (
                                <div className="col-span-full">
                                  <p className="text-xs text-content-subtle mb-1">Detected Packages</p>
                                  <span className="inline-flex px-2 py-0.5 rounded bg-danger-soft text-danger text-xs border border-danger/30">
                                    Suspicious app found (SDK sends no names)
                                  </span>
                                </div>
                              )}
                              {d.detected_packages && (
                                <div className="col-span-full">
                                  <p className="text-xs text-content-subtle mb-1">Detected Packages</p>
                                  <div className="flex flex-wrap gap-1.5">
                                    {d.detected_packages.split(',').map((pkg: string) => (
                                      <span key={pkg} className="inline-flex px-2 py-0.5 rounded bg-danger-soft text-danger text-xs font-mono border border-danger/30">
                                        {pkg.trim()}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-border">
                <p className="text-sm text-content-subtle">{total} devices total</p>
                <div className="flex gap-1">
                  <button
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page === 1}
                    className="px-3 py-1 text-sm rounded border border-border disabled:opacity-40 hover:bg-surface-muted"
                  >
                    Prev
                  </button>
                  <span className="px-3 py-1 text-sm text-content-subtle">
                    {page} / {totalPages}
                  </span>
                  <button
                    onClick={() => setPage(Math.min(totalPages, page + 1))}
                    disabled={page === totalPages}
                    className="px-3 py-1 text-sm rounded border border-border disabled:opacity-40 hover:bg-surface-muted"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
